import { useState, useEffect, useCallback } from "react";
import { FiSearch, FiArrowLeft, FiChevronRight, FiPrinter, FiAlertTriangle, FiFilter, FiCheck } from "react-icons/fi";
import { apiGet } from "../api";
import { PageHeader, Card, StatCard, Pill, Btn, THead, TD, fmtInvNo, Pagination, PER_PAGE_DEFAULT } from "./KioskUI";

const MMK = (n) => Number(n).toLocaleString("en-US", { maximumFractionDigits: 0 });
const fmtDate = (s) => new Date(s).toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" });
const fmtDateLong = (s) => new Date(s).toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" });

/* status inferred: credit sales may be open, cash sales are always paid */
const saleStatus = (s) => s.is_credit ? "open" : "paid";

/* ─── Print invoice ─── */
function printInvoice(inv) {
  const rows = inv.items.map(item => `
    <tr>
      <td>${item.product_name}<span class="sku">${item.sku || ""}</span></td>
      <td class="r">${item.quantity}</td>
      <td class="r">${MMK(item.unit_price)}</td>
      <td class="r">${MMK(item.total)}</td>
    </tr>`).join("");

  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${fmtInvNo(inv.sale_id)}</title>
  <style>
    *{margin:0;padding:0;box-sizing:border-box;font-family:-apple-system,Segoe UI,Roboto,sans-serif}
    body{padding:40px 46px;color:#131820}
    .top{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #131820;padding-bottom:18px}
    .biz{font-size:22px;font-weight:800;letter-spacing:-.5px}.sub{font-size:12px;color:#697583;margin-top:3px}
    .inv{text-align:right}.inv .n{font-size:18px;font-weight:800;color:#1D4ED8}.inv .d{font-size:12px;color:#697583;margin-top:4px}
    .meta{display:flex;gap:48px;margin:22px 0 18px}
    .meta .l{font-size:11px;text-transform:uppercase;letter-spacing:.05em;color:#94A0AE;font-weight:700}
    .meta .v{font-size:14px;font-weight:600;margin-top:4px}
    table{width:100%;border-collapse:collapse;margin-top:6px}
    th{text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:.04em;color:#94A0AE;padding:9px 10px;border-bottom:1px solid #DCE2EA}
    th.r,td.r{text-align:right}
    td{padding:11px 10px;border-bottom:1px solid #EAEEF3;font-size:13.5px}
    td .sku{display:block;font-size:11px;color:#94A0AE;margin-top:2px}
    .totals{margin-left:auto;width:280px;margin-top:18px}
    .totals .row{display:flex;justify-content:space-between;font-size:13.5px;padding:5px 0;color:#4A5563}
    .totals .grand{border-top:2px solid #131820;margin-top:6px;padding-top:10px;font-size:18px;font-weight:800;color:#131820}
    .pill{display:inline-block;font-size:11px;font-weight:700;padding:3px 10px;border-radius:99px;background:#EEF3FF;color:#1D4ED8}
    .foot{margin-top:40px;font-size:11.5px;color:#94A0AE;text-align:center;border-top:1px solid #EAEEF3;padding-top:16px}
  </style></head><body>
  <div class="top">
    <div><div class="biz">Hein Khant Zaw</div><div class="sub">Clothing Wholesale · Yangon</div></div>
    <div class="inv"><div class="n">${fmtInvNo(inv.sale_id)}</div><div class="d">${fmtDateLong(inv.date)}</div></div>
  </div>
  <div class="meta">
    <div><div class="l">Billed to</div><div class="v">${inv.customer?.name || "Walk-in customer"}</div></div>
    <div><div class="l">Contact</div><div class="v">${inv.customer?.phone || "—"}</div></div>
    <div><div class="l">Payment</div><div class="v"><span class="pill">${inv.payment_type}</span></div></div>
    ${inv.location ? `<div><div class="l">Location</div><div class="v">${inv.location}</div></div>` : ""}
  </div>
  <table><thead><tr><th>Product</th><th class="r">Qty</th><th class="r">Unit (MMK)</th><th class="r">Amount</th></tr></thead>
  <tbody>${rows}</tbody></table>
  <div class="totals">
    <div class="row"><span>Subtotal</span><span>${MMK(inv.subtotal)} Ks</span></div>
    ${inv.discount_amount > 0 ? `<div class="row"><span>Discount</span><span>– ${MMK(inv.discount_amount)} Ks</span></div>` : ""}
    ${inv.transportation_fee > 0 ? `<div class="row"><span>Transportation</span><span>+ ${MMK(inv.transportation_fee)} Ks</span></div>` : ""}
    <div class="row grand"><span>Total</span><span>${MMK(inv.total_amount)} MMK</span></div>
  </div>
  <div class="foot">Thank you for your business · ${fmtInvNo(inv.sale_id)}</div>
  <div class="no-print" style="margin-top:32px;text-align:center">
    <button onclick="window.close()" style="padding:10px 28px;font-size:14px;border:1px solid #ddd;border-radius:8px;background:#f4f6f9;cursor:pointer">✕ Close without printing</button>
  </div>
  <style>@media print{.no-print{display:none}}</style>
  </body></html>`;

  const w = window.open("", "_blank", "width=720,height=900");
  if (!w) { alert("Please allow pop-ups to print the invoice."); return; }
  w.document.open(); w.document.write(html); w.document.close();
  w.focus();
  setTimeout(() => {
    try { w.print(); } catch (e) {}
    w.onafterprint = () => w.close();
  }, 350);
}

/* ─── Invoice detail ─── */
export function InvoiceDetail({ saleId, token, go, onBack }) {
  const [inv, setInv]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    apiGet(`/api/inventory/sales/${saleId}/invoice`, token)
      .then(r => r.json())
      .then(d => { if (d.error) setError(d.error); else setInv(d); })
      .catch(() => setError("Cannot load invoice."))
      .finally(() => setLoading(false));
  }, [saleId, token]);

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 240 }}>
      <span style={{ color: "var(--sub)", fontSize: 14 }}>Loading…</span>
    </div>
  );

  if (error || !inv) return (
    <div style={{ textAlign: "center", padding: "48px 0", color: "var(--danger)" }}>{error || "Invoice not found."}</div>
  );

  const isCredit = inv.payment_type === "Credit";
  const status   = isCredit ? "open" : "paid";

  return (
    <div style={{ maxWidth: 880, margin: "0 auto" }}>
      {/* header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 22 }}>
        <button onClick={onBack} style={{ border: "1px solid var(--line)", background: "var(--surface)", borderRadius: "var(--r-sm)", padding: 8, cursor: "pointer", display: "flex" }}>
          <FiArrowLeft size={18} color="var(--sub)" />
        </button>
        <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 11, minWidth: 0 }}>
          <h1 style={{ fontFamily: "var(--font-head)", fontSize: 25, fontWeight: 800, letterSpacing: "-.025em", margin: 0, color: "var(--ink)", whiteSpace: "nowrap" }}>{fmtInvNo(inv.sale_id)}</h1>
          <Pill tone={isCredit ? "credit" : "cash"}>{inv.payment_type}</Pill>
          <Pill tone={status === "open" ? "low" : "ok"}>{status === "open" ? "Open" : "Paid"}</Pill>
        </div>
        <Btn kind="ghost" onClick={() => printInvoice(inv)} style={{ flexShrink: 0 }}>
          <FiPrinter size={16} /> Print
        </Btn>
      </div>

      <Card style={{ padding: 0, overflow: "hidden" }}>
        {/* business header */}
        <div style={{ padding: "22px 26px", borderBottom: "1px solid var(--hair)", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 24 }}>
          <div>
            <div style={{ fontFamily: "var(--font-head)", fontSize: 19, fontWeight: 800, color: "var(--ink)", letterSpacing: "-.02em" }}>Hein Khant Zaw</div>
            <div style={{ fontSize: 12.5, color: "var(--sub)", marginTop: 3 }}>Clothing Wholesale · Yangon</div>
          </div>
          <div style={{ textAlign: "right", flexShrink: 0 }}>
            <div style={{ fontFamily: "var(--font-head)", fontSize: 18, fontWeight: 800, color: "var(--primary)" }}>{fmtInvNo(inv.sale_id)}</div>
            <div style={{ fontSize: 12.5, color: "var(--sub)", marginTop: 3 }}>{fmtDateLong(inv.date)}</div>
          </div>
        </div>

        {/* meta row */}
        <div style={{ padding: "18px 26px", borderBottom: "1px solid var(--hair)", display: "flex", gap: 48, flexWrap: "wrap" }}>
          <MetaField label="Billed to" value={inv.customer?.name || "Walk-in customer"} />
          <MetaField label="Contact" value={inv.customer?.phone || "—"} />
          <MetaField label="Payment" value={inv.payment_type} />
          {inv.employee && <MetaField label="Cashier" value={inv.employee} />}
          {inv.location && <MetaField label="Location" value={inv.location} />}
        </div>

        {/* line items */}
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <THead cols={[{ t: "Product" }, { t: "SKU" }, { t: "Qty", r: 1 }, { t: "Unit price", r: 1 }, { t: "Amount", r: 1 }]} />
          <tbody>
            {inv.items.map((item, i) => (
              <tr key={i}>
                <TD b>{item.product_name}</TD>
                <TD mut>{item.sku || "—"}</TD>
                <TD r>{item.quantity}</TD>
                <TD r mut>{MMK(item.unit_price)} Ks</TD>
                <TD r b>{MMK(item.total)} Ks</TD>
              </tr>
            ))}
          </tbody>
        </table>

        {/* totals */}
        <div style={{ padding: "18px 26px", background: "var(--faint)", display: "flex", justifyContent: "flex-end" }}>
          <div style={{ width: 300 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13.5, color: "var(--sub)", padding: "5px 0" }}>
              <span>Subtotal</span>
              <span style={{ fontVariantNumeric: "tabular-nums" }}>{MMK(inv.subtotal)} Ks</span>
            </div>
            {inv.discount_amount > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13.5, color: "var(--warning)", padding: "5px 0" }}>
                <span>Discount{inv.discount_percentage > 0 ? ` (${inv.discount_percentage}%)` : ""}</span>
                <span style={{ fontVariantNumeric: "tabular-nums" }}>– {MMK(inv.discount_amount)} Ks</span>
              </div>
            )}
            {inv.transportation_fee > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13.5, color: "var(--sub)", padding: "5px 0" }}>
                <span>Transportation</span>
                <span style={{ fontVariantNumeric: "tabular-nums" }}>+ {MMK(inv.transportation_fee)} Ks</span>
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginTop: 6, paddingTop: 11, borderTop: "2px solid var(--ink)" }}>
              <span style={{ fontFamily: "var(--font-head)", fontSize: 15, fontWeight: 800 }}>Total</span>
              <span style={{ fontFamily: "var(--font-head)", fontSize: 23, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>
                {MMK(inv.total_amount)} <span style={{ fontSize: 12.5, color: "var(--sub)" }}>MMK</span>
              </span>
            </div>
          </div>
        </div>
      </Card>

      {/* open credit banner + customer link */}
      {(status === "open" || inv.customer) && (
        <div style={{ display: "flex", gap: 12, marginTop: 16, alignItems: "center", flexWrap: "wrap" }}>
          {status === "open" && (
            <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 11, padding: "14px 18px", background: "var(--warning-bg)", borderRadius: "var(--r-md)", minWidth: 200 }}>
              <FiAlertTriangle size={19} color="var(--warning)" style={{ flexShrink: 0 }} />
              <div style={{ fontSize: 13.5, color: "var(--warning)", fontWeight: 600 }}>
                Open credit invoice — {MMK(inv.total_amount)} Ks outstanding on this sale.
              </div>
            </div>
          )}
          {inv.customer?.name && inv.customer.name !== "Walk-in Customer" && (
            <Btn kind="soft" icon="customers" onClick={() => go("customers")} style={{ flexShrink: 0 }}>
              View {inv.customer.name.split(" ")[0]}
            </Btn>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Filter button ─── */
function FilterButton({ typeFilter, locationFilter, locationOptions, onTypeChange, onLocationChange, onClear }) {
  const [open, setOpen] = useState(false);
  const activeCount = (typeFilter !== "all" ? 1 : 0) + (locationFilter !== "all" ? 1 : 0);

  return (
    <div style={{ position: "relative" }}>
      <button onClick={() => setOpen(v => !v)}
        style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 14px", border: `1px solid ${activeCount > 0 ? "var(--primary)" : "var(--line)"}`, borderRadius: "var(--r-sm)", background: activeCount > 0 ? "var(--blue-50)" : "var(--surface)", cursor: "pointer", fontFamily: "var(--font-body)", fontSize: 14, fontWeight: 600, color: activeCount > 0 ? "var(--primary)" : "var(--n-600)" }}>
        <FiFilter size={15} /> Filter
        {activeCount > 0 && (
          <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", minWidth: 18, height: 18, borderRadius: 9, background: "var(--primary)", color: "#fff", fontSize: 11, fontWeight: 700, padding: "0 5px" }}>
            {activeCount}
          </span>
        )}
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 30 }} />
          <div style={{ position: "absolute", top: "calc(100% + 6px)", right: 0, background: "var(--surface)", border: "1px solid var(--line)", borderRadius: "var(--r-md)", boxShadow: "var(--shadow-md)", zIndex: 40, width: 260, padding: 16 }}>
            <FilterSection label="Payment type" options={[["all", "All"], ["cash", "Cash"], ["credit", "Credit"]]} value={typeFilter} onChange={onTypeChange} />
            {locationOptions.length > 1 && (
              <FilterSection
                label="Location"
                options={[["all", "All locations"], ...locationOptions.map(l => [l, l])]}
                value={locationFilter} onChange={onLocationChange}
                style={{ marginTop: 16 }}
              />
            )}
            {activeCount > 0 && (
              <button onClick={onClear}
                style={{ marginTop: 16, width: "100%", padding: "8px", border: "none", borderTop: "1px solid var(--hair)", background: "transparent", color: "var(--danger)", fontSize: 13, fontWeight: 600, cursor: "pointer", textAlign: "center" }}>
                Clear filters
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function FilterSection({ label, options, value, onChange, style }) {
  return (
    <div style={style}>
      <div style={{ fontSize: 11.5, fontWeight: 700, color: "var(--sub)", letterSpacing: ".02em", marginBottom: 8 }}>{label}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {options.map(([id, t]) => {
          const on = value === id;
          return (
            <button key={id} onClick={() => onChange(id)}
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 10px", borderRadius: "var(--r-sm)", border: "none", background: on ? "var(--blue-50)" : "transparent", cursor: "pointer", fontFamily: "var(--font-body)", fontSize: 13.5, fontWeight: on ? 700 : 500, color: on ? "var(--primary)" : "var(--ink)", textAlign: "left" }}
              onMouseEnter={e => { if (!on) e.currentTarget.style.background = "var(--faint)"; }}
              onMouseLeave={e => { if (!on) e.currentTarget.style.background = "transparent"; }}
            >
              {t}
              {on && <FiCheck size={14} color="var(--primary)" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function MetaField({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".05em", textTransform: "uppercase", color: "var(--n-400)" }}>{label}</div>
      <div style={{ fontSize: 14.5, fontWeight: 600, color: "var(--ink)", marginTop: 4 }}>{value}</div>
    </div>
  );
}

/* ─── Invoice list ─── */
export default function Invoices({ token, go }) {
  const [sales, setSales]     = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ]             = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [locationFilter, setLocationFilter] = useState("all");
  const [page, setPage]       = useState(1);
  const [view, setView]       = useState("list");
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    apiGet("/api/inventory/locations", token)
      .then(r => r.json())
      .then(d => setLocations(Array.isArray(d) ? d : []))
      .catch(() => setLocations([]));
  }, [token]);

  const fetchSales = useCallback(async () => {
    try {
      const r = await apiGet("/api/inventory/sales", token);
      const d = await r.json();
      setSales(Array.isArray(d) ? d : []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchSales(); }, [fetchSales]);

  if (view === "detail") {
    return (
      <InvoiceDetail
        saleId={selectedId}
        token={token}
        go={go}
        onBack={() => { setView("list"); setSelectedId(null); }}
      />
    );
  }

  /* stats */
  const grand   = sales.reduce((s, i) => s + Number(i.total_amount), 0);
  const openSales = sales.filter(i => i.is_credit);
  const openSum = openSales.reduce((s, i) => s + Number(i.total_amount), 0);

  /* every location the business has, plus any name seen on an old sale
     record (e.g. a since-renamed or deleted location) so history still filters */
  const locationOptions = [...new Set([...locations.map(l => l.name), ...sales.map(i => i.location).filter(Boolean)])].sort();

  /* filtered rows */
  const qq = q.trim().toLowerCase();
  const filtered = sales
    .filter(i => typeFilter === "all" || (typeFilter === "cash" ? !i.is_credit : i.is_credit))
    .filter(i => locationFilter === "all" || i.location === locationFilter)
    .filter(i => !qq || fmtInvNo(i.sale_id).toLowerCase().includes(qq) || (i.customer_name || "").toLowerCase().includes(qq));
  const rows = filtered.slice((page - 1) * PER_PAGE_DEFAULT, page * PER_PAGE_DEFAULT);

  return (
    <div>
      <PageHeader title="Invoices" mm="ဘောက်ချာ" desc={`${sales.length} invoice${sales.length !== 1 ? "s" : ""} · every sale on record`}>
        <div style={{ display: "flex", alignItems: "center", gap: 9, background: "var(--surface)", border: "1px solid var(--line)", borderRadius: "var(--r-sm)", padding: "9px 13px", width: 280 }}>
          <FiSearch size={16} color="var(--n-400)" style={{ flexShrink: 0 }} />
          <input value={q} onChange={e => { setQ(e.target.value); setPage(1); }} placeholder="Search invoice # or customer…"
            style={{ border: "none", outline: "none", background: "transparent", fontFamily: "var(--font-body)", fontSize: 14, color: "var(--ink)", width: "100%" }} />
        </div>
        <FilterButton
          typeFilter={typeFilter} locationFilter={locationFilter} locationOptions={locationOptions}
          onTypeChange={v => { setTypeFilter(v); setPage(1); }}
          onLocationChange={v => { setLocationFilter(v); setPage(1); }}
          onClear={() => { setTypeFilter("all"); setLocationFilter("all"); setPage(1); }}
        />
        <Btn kind="acc" icon="plus" onClick={() => go("sale")}>New Sale</Btn>
      </PageHeader>

      {/* stat cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 16, marginBottom: 18 }}>
        <StatCard label="Total Invoiced" value={(grand / 1e6).toFixed(2) + "M"} unit="MMK" icon="invoice" tone="blue" sub={MMK(grand) + " Ks"} />
        <StatCard label="Open (credit)" value={openSales.length} icon="alert" tone="amber" sub="awaiting payment" />
        <StatCard label="Outstanding" value={(openSum / 1e6).toFixed(2) + "M"} unit="MMK" icon="payments" tone="amber" sub={MMK(openSum) + " Ks owed"} />
      </div>

      {/* table */}
      <Card style={{ overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: "48px 20px", textAlign: "center", color: "var(--sub)", fontSize: 14 }}>Loading…</div>
        ) : (
          <>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <THead cols={[
              { t: "Invoice" }, { t: "Date" }, { t: "Customer" }, { t: "Location" },
              { t: "Items", r: 1 }, { t: "Type" }, { t: "Status" },
              { t: "Total", r: 1 }, { t: "", r: 1, w: 40 },
            ]} />
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={9} style={{ padding: "32px 16px", textAlign: "center", color: "var(--n-400)", fontSize: 14 }}>
                  {qq ? "No invoices match." : "No invoices yet."}
                </td></tr>
              )}
              {rows.map(s => {
                const status = saleStatus(s);
                return (
                  <tr key={s.sale_id} style={{ cursor: "pointer" }}
                    onClick={() => { setSelectedId(s.sale_id); setView("detail"); }}
                    onMouseEnter={e => e.currentTarget.style.background = "var(--faint)"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                  >
                    <TD b>{fmtInvNo(s.sale_id)}</TD>
                    <TD mut>{fmtDate(s.date)}</TD>
                    <TD>{s.customer_name || "Walk-in"}</TD>
                    <TD mut>{s.location || "—"}</TD>
                    <TD r mut>{s.items_count}</TD>
                    <td style={{ padding: "14px 16px", borderTop: "1px solid var(--hair)" }}>
                      <Pill tone={s.is_credit ? "credit" : "cash"}>{s.payment_type}</Pill>
                    </td>
                    <td style={{ padding: "14px 16px", borderTop: "1px solid var(--hair)" }}>
                      <Pill tone={status === "open" ? "low" : "ok"}>{status === "open" ? "Open" : "Paid"}</Pill>
                    </td>
                    <TD r b>{MMK(s.total_amount)}</TD>
                    <td style={{ padding: "14px 16px", borderTop: "1px solid var(--hair)", textAlign: "right" }}>
                      <FiChevronRight size={17} color="var(--n-300)" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <Pagination total={filtered.length} page={page} onChange={setPage} />
          </>
        )}
      </Card>
    </div>
  );
}
